import {
  CloseCircleIcon,
  EditIcon,
  IconClickEvent,
  PlusCircleIcon,
  ResetIcon,
} from '@mm/icons';
import alertAction from 'assets/icons/alert-action.svg';
import classnames from 'classnames';
import { isEqual, map } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { AudienceFilterItemInput } from '../../../../../../../common/components/AudienceFilterItemInput/AudienceFilterItemInput';
import FilterableSelect from '../../../../../../../common/components/FilterableSelect/FilterableSelect';
import { ItemLike } from '../../../../../../../common/components/FlatMenu/FlatMenu';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useInputRef } from '../../../../../../../common/widgets/common/use-input-ref';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useLeadAssignmentsCRUDActions } from '@mm/state/actions/LeadAssignmentsCRUDActions';
import { LeadAssignment } from '@mm/state/interfaces/leadAssignments';
import AudiencesModel, { FilterItem } from '@mm/state/model/AudiencesModel';
import TagModel from '@mm/state/model/TagModel';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { allTags } from '@mm/state/selectors/tags';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import SelectAudiences from '../../../../../campaigns/components/SelectAudiences/SelectAudiences';
import { colors } from '../../../../styles/colors';
import './SelectInputWithPopup.scss';

type Props = {
  record: LeadAssignment;
  isExpanded: boolean;
  fieldsRequired: boolean;
};

export const SelectInputWithPopup = (props: Props) => {
  const { record, isExpanded, fieldsRequired } = props;
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);
  const [isEditAttributesModalOpen, setIsEditAttributesModalOpen] =
    useState(false);
  const [isEditAudiencesModalOpen, setIsEditAudiencesModalOpen] =
    useState(false);
  const [selectedTags, setSelectedTags] = useState<TagModel[]>(
    record.tags || [],
  );
  const { getAudienceVariable } = useAudiencesCRUDActions();

  const [searchTagQuery, setSearchTagQuery] = useState('');
  const availableTags = useSelector(allTags);
  const tagSuggestions = useMemo(() => {
    return [
      {
        title: '',
        suggestions: selectedTags.length
          ? availableTags.filter(
              availableTag =>
                !selectedTags.some(tag => tag.name === availableTag.name),
            )
          : availableTags,
      },
    ];
  }, [availableTags, selectedTags]);

  const blankFilterItem: FilterItem = {
    filterType: '',
    operator: 'equal',
    value: '',
    variableId: 0,
    variableType: 'SystemVariable',
    variable: '',
  };

  const [attributeFilter, setAttributeFilter] =
    useState<FilterItem>(blankFilterItem);

  const audiences = useSelector(selectAudienceSorted);
  const audiencesFromRecord = useMemo(() => {
    return audiences.filter(audience =>
      record.audienceIds?.some(id => id === audience.id),
    );
  }, [audiences, record.audienceIds]);
  const [selectedAudiences, setSelectedAudiences] =
    useState<AudiencesModel[]>(audiencesFromRecord);

  const closeAll = () => {
    setIsEditTagsModalOpen(false);
    setIsEditAttributesModalOpen(false);
    setIsEditAudiencesModalOpen(false);
  };

  useEffect(() => {
    audiencesCRUDActions.retrieveAll(activeChatbotId);
    getAudienceVariable(activeChatbotId);
  }, []);

  useEffect(() => {
    setSelectedAudiences(audiencesFromRecord);
  }, [audiencesFromRecord]);

  useEffect(() => {
    if (!isExpanded) {
      closeAll();
    }
  }, [isExpanded]);

  useEffect(() => {
    setSelectedTags(record.tags || []);
  }, [record.tags]);

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const { update } = useLeadAssignmentsCRUDActions();
  const { inputRef, memoizedSetInputRef } = useInputRef(true);

  const audiencesId = map(audiences, 'id');

  const isAnyContact = record.type === 'any_contact';
  const modes = {
    tags: {
      name: 'Tags',
      setIsOpen: setIsEditTagsModalOpen,
      items: record.tags.map(tag => {
        return {
          id: tag.name,
          name: tag.name,
        };
      }),
      isEqual: isEqual(map(record.tags, 'name'), map(selectedTags, 'name')),
      setSelectedItems: setSelectedTags,
      clear: () => setSelectedTags([]),
      save: () => {
        update(activeChatbotId, record.id, {
          tags: selectedTags,
        });
        setIsEditTagsModalOpen(false);
      },
      cancel: () => {
        setSelectedTags(record.tags);
        setIsEditTagsModalOpen(false);
      },
      onRemoveItem: (tagName: string) => {
        const tags = record.tags.filter(tag => tag.name !== tagName);
        update(activeChatbotId, record.id, {
          tags,
        });
      },
    },
    attributes: {
      name: 'Attributes',
      setIsOpen: setIsEditAttributesModalOpen,
      items: record.variableFilters
        ? record.variableFilters[0]?.value.map(value => {
            return {
              id: value,
              name: value,
            };
          }) ?? []
        : [],
      isEqual:
        isEqual(
          record.variableFilters && record.variableFilters[0]?.value,
          Array.isArray(attributeFilter.value)
            ? attributeFilter.value
            : [attributeFilter.value],
        ) &&
        !!record.variableFilters &&
        record.variableFilters[0]?.operator === attributeFilter.operator,
      setSelectedItems: setAttributeFilter,
      clear: () => setAttributeFilter(blankFilterItem),
      save: () => {
        update(activeChatbotId, record.id, {
          variableFilters: [
            {
              ...attributeFilter,
              value: Array.isArray(attributeFilter.value)
                ? attributeFilter.value
                : [attributeFilter.value],
            },
          ],
        });
        setIsEditAttributesModalOpen(false);
      },

      cancel: () => {
        setAttributeFilter(blankFilterItem);
        setIsEditAttributesModalOpen(false);
      },
      onRemoveItem: (attribute: string) => {
        const variableFilter =
          record.variableFilters && record.variableFilters[0];
        if (variableFilter) {
          const variableFiltersValues = variableFilter.value.filter(
            value => value !== attribute,
          );
          const variableFiltersUpdated = variableFiltersValues.length
            ? [
                {
                  ...variableFilter,
                  value: variableFiltersValues,
                },
              ]
            : [];
          update(activeChatbotId, record.id, {
            variableFilters: variableFiltersUpdated,
          });
        }
      },
    },
    audiences: {
      name: 'Audiences',
      setIsOpen: setIsEditAudiencesModalOpen,
      items: audiencesFromRecord.map(audience => {
        return {
          id: String(audience.id),
          name: audience.name,
        };
      }),
      isEqual: isEqual(
        map(audiencesFromRecord, 'id'),
        map(selectedAudiences, 'id'),
      ),
      setSelectedItems: selectedAudiences,
      clear: () => setSelectedAudiences([]),
      save: () => {
        update(activeChatbotId, record.id, {
          audienceIds: map(selectedAudiences, 'id'),
        });
        setIsEditAudiencesModalOpen(false);
      },
      cancel: () => {
        setSelectedAudiences(audiencesFromRecord);
        setIsEditAudiencesModalOpen(false);
      },
      onRemoveItem: (audienceId: string) => {
        const filteredAudiences = audiencesFromRecord.filter(
          audience => audience.id !== Number(audienceId),
        );
        update(activeChatbotId, record.id, {
          audienceIds: map(filteredAudiences, 'id'),
        });
      },
    },
  };

  const onTagAdd = (tag?: TagModel) => {
    if (tag) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const onTagRemove = (e: IconClickEvent, tag?: string) => {
    e.stopPropagation();
    if (tag) {
      setSelectedTags(selectedTags.filter(t => t.name !== tag));
    }
  };

  const onAudienceRemove = (e: IconClickEvent, audience: string) => {
    e.stopPropagation();
    setSelectedAudiences(selectedAudiences.filter(t => t.name !== audience));
  };

  const [selectedSuggestion, setSelectedSuggestion] = useState<
    ItemLike | undefined
  >();
  const [selectId, setSelectId] = useState(1);

  const editModalContent = (mode: 'tags' | 'attributes' | 'audiences') => {
    switch (mode) {
      case 'tags': {
        return (
          <div className="tags-container">
            <div className="tags-container__header">
              <FilterableSelect
                key={selectId}
                className="edit-modal__tags-select"
                defaultValue={searchTagQuery}
                onInput={e => setSearchTagQuery(e.currentTarget.value)}
                onChange={(_, suggestion) => setSelectedSuggestion(suggestion)}
                name="filterable"
                searchPlaceholder="Add tag"
                suggestions={tagSuggestions}
              />
              <PrimaryButton
                color={ButtonColor.blue}
                onClick={() => {
                  if (selectedSuggestion) {
                    onTagAdd(selectedSuggestion);
                    setSelectId(selectId + 1);
                  }
                }}
              >
                Add
              </PrimaryButton>
            </div>
            <div className="select-input__items-container">
              {selectedTags.map(item => {
                return (
                  <div className="select-input__item" key={item.name}>
                    <div>{item.name}</div>

                    <CloseCircleIcon
                      className="select-input__item-remove"
                      onClick={e => onTagRemove(e, item.name)}
                      color="#009EFF"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      case 'attributes': {
        return (
          <AudienceFilterItemInput
            className="advanced-filters__attribute-filter"
            filter={attributeFilter}
            setFilter={setAttributeFilter}
            showBlankVariableOption
            setInputRef={memoizedSetInputRef}
          />
        );
      }
      case 'audiences': {
        return (
          <>
            <SelectAudiences
              showBadge={false}
              disableDeleting={false}
              heading="Audiences"
              originalAudiences={audiencesId}
              selectedAudiences={selectedAudiences}
              onAudiencesSelected={setSelectedAudiences}
            />
            <div className="select-input__items-container">
              {selectedAudiences.map(item => {
                return (
                  <div className="select-input__item" key={item.name}>
                    <div>{item.name}</div>

                    <CloseCircleIcon
                      className="select-input__item-remove"
                      onClick={e => onAudienceRemove(e, item.name)}
                      color="#009EFF"
                    />
                  </div>
                );
              })}
            </div>
          </>
        );
      }
      default:
        return undefined;
    }
  };

  const editModal = (mode: 'tags' | 'attributes' | 'audiences') => {
    const { name, clear, save, cancel, isEqual } = modes[mode];
    return (
      <div
        className={classnames('edit-modal', {
          'edit-modal--hide': !isExpanded,
        })}
        onClick={event => {
          const { className = '' } = event.target as Element;
          const shouldStayFocused =
            typeof className === 'string' &&
            className.includes(
              'advanced-filters__attribute-filter__suggest-input',
            );
          if (!shouldStayFocused) {
            inputRef?.blur();
          }
        }}
      >
        <div>
          <div className="edit-modal__name">{name}</div>
          {editModalContent(mode)}
        </div>
        <div className="edit-modal__actions">
          <div className="edit-modal__action-clear" onClick={clear}>
            <ResetIcon className="edit-modal__action-clear-icon" />
            Clear all
          </div>
          <div className="edit-modal__action-save-cancel-container">
            <div className="edit-modal__action-cancel" onClick={cancel}>
              Cancel
            </div>

            <PrimaryButton
              className="edit-modal__action-save"
              color={ButtonColor.secondaryBlue}
              onClick={() => {
                save();
              }}
              disabled={isEqual}
            >
              Save
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  };

  const inputComponent = (mode: 'tags' | 'attributes' | 'audiences') => {
    const { name, setIsOpen, items, onRemoveItem } = modes[mode];

    return (
      <div
        className={classnames('select-input', {
          'select-input--disabled': isAnyContact,
        })}
      >
        <div className="select-input__wrapper">
          <span className="select-input__name">
            {fieldsRequired && (
              <>
                <img
                  className="select-input__error-icon"
                  id="fieldsRequired"
                  src={alertAction}
                />
                <UncontrolledTooltip
                  className="widget-component__tooltip"
                  innerClassName="widget-component__inner-tooltip"
                  target="fieldsRequired"
                >
                  {'Required fields are missing'}
                </UncontrolledTooltip>
              </>
            )}{' '}
            {name}:
          </span>
          <div className="select-input__input">
            <div
              className="select-input__items-container"
              onClick={() => {
                closeAll();
                if (!isAnyContact) {
                  setIsOpen(true);
                }
              }}
            >
              {items.map((item, index) => {
                return (
                  <div
                    key={item.id}
                    className="select-input__item-or-container"
                  >
                    {index !== 0 && (
                      <span className="select-input__items-or">or</span>
                    )}
                    <div className="select-input__item" key={item.id}>
                      <div>{item.name}</div>

                      <CloseCircleIcon
                        className="select-input__item-remove"
                        color="#009EFF"
                        onClick={() => onRemoveItem(item.id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {items.length ? (
              <EditIcon />
            ) : (
              <PlusCircleIcon color={colors.palePurple} />
            )}
          </div>
        </div>
        {mode !== 'audiences' && (
          <div className="select-input__and-component">
            AND
            <div className="select-input__and-line" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PrimaryToolTip
        position="right"
        align="start"
        withoutLine
        anchorElement={inputComponent('tags')}
        isVisible={isEditTagsModalOpen}
        setIsVisible={setIsEditTagsModalOpen}
        toolTipDisabled={isAnyContact}
      >
        {editModal('tags')}
      </PrimaryToolTip>
      <PrimaryToolTip
        position="right"
        align="start"
        withoutLine
        anchorElement={inputComponent('attributes')}
        isVisible={isEditAttributesModalOpen}
        setIsVisible={setIsEditAttributesModalOpen}
        toolTipDisabled={isAnyContact}
      >
        {editModal('attributes')}
      </PrimaryToolTip>
      <PrimaryToolTip
        position="right"
        align="start"
        withoutLine
        anchorElement={inputComponent('audiences')}
        isVisible={isEditAudiencesModalOpen}
        setIsVisible={setIsEditAudiencesModalOpen}
        toolTipDisabled={isAnyContact}
      >
        {editModal('audiences')}
      </PrimaryToolTip>
    </div>
  );
};
